import { AgGridColumnProps } from 'ag-grid-react';

export type SortDirection = 'ASC' | 'DESC';

export type SortOption = {
  label: string;
  value: string;
};

export const SORT_DIRECTION_OPTIONS: { label: string; value: SortDirection }[] = [
  { label: 'Descending', value: 'DESC' },
  { label: 'Ascending', value: 'ASC' },
];

const LEAD_SORT_OPTIONS: SortOption[] = [
  { label: 'Fit Score', value: 'fitScore' },
  { label: 'Engagement Score', value: 'engagementScore' },
  { label: 'Fit & Engagement Score', value: 'compositeScore' },
  { label: 'Last Incoming Date', value: 'lastIncomingDate' },
  { label: 'Last Email', value: 'lastEmailDate' },
  { label: 'Last Call', value: 'lastCallDate' },
  { label: 'Last Text', value: 'lastTextDate' },
  { label: 'Lead Source', value: 'leadSource' },
  { label: 'Status', value: 'status' },
  { label: 'Sales User Tag', value: 'salesUserTag' },
  { label: 'Brokered', value: 'brokered' },
  { label: 'Journey Status', value: 'journeyBuilderStatus' },
];

// Date options shown after the dashboard columns
const LEAD_TRAILING_SORT_OPTIONS: SortOption[] = [
  { label: 'Created Date', value: 'createdDate' },
  { label: 'Last Assigned Date', value: 'lastAssignedDate' },
  { label: 'Last Modified', value: 'lastUpdatedDate' },
];

// Non-lead dashboards (borrower, broker, TPO, ...) only get these
const OTHER_DASHBOARDS_SORT_OPTIONS: SortOption[] = [
  { label: 'Last Email', value: 'lastEmailDate' },
  { label: 'Last Call', value: 'lastCallDate' },
  { label: 'Last Text', value: 'lastTextDate' },
];

// Preferred default, first one available for the dashboard wins
const DEFAULT_SORT_PREFERENCE = ['compositeScore', 'lastEmailDate'];

// Contact info and do-not-contact flags are not offered as sort options
const EXCLUDED_SORT_FIELDS = [
  'name',
  'fullName',
  'email',
  'phone',
  'company',
  'doNotCall',
  'doNotSendEmail',
  'doNotSms',
  'doNotMail',
];

// Columns that were sortable in the grid are the ones offered in the dropdown
const isSortableColumn = (column: AgGridColumnProps) =>
  !!column.field &&
  column.sortable !== false &&
  !column.hide &&
  !EXCLUDED_SORT_FIELDS.includes(column.field) &&
  !!column.sortingOrder;

export const getSortOptions = (
  isLeadDashboard: boolean,
  columns: AgGridColumnProps[] = []
): SortOption[] => {
  if (!isLeadDashboard) {
    return OTHER_DASHBOARDS_SORT_OPTIONS;
  }
  const options: SortOption[] = [...LEAD_SORT_OPTIONS];
  const isListed = (value: string) =>
    [...options, ...LEAD_TRAILING_SORT_OPTIONS].some(
      option => option.value === value
    );
  columns.filter(isSortableColumn).forEach(column => {
    if (!isListed(column.field)) {
      options.push({ label: column.headerName ?? column.field, value: column.field });
    }
  });
  return [...options, ...LEAD_TRAILING_SORT_OPTIONS];
};

export const getDefaultSortBy = (options: SortOption[]) => {
  const preferred = DEFAULT_SORT_PREFERENCE.find(value =>
    options.some(option => option.value === value)
  );
  return preferred ?? options[0]?.value;
};

export const DEFAULT_SORT_BY = DEFAULT_SORT_PREFERENCE[0];
export const DEFAULT_FALLBACK_SORT_BY = DEFAULT_SORT_PREFERENCE[1];
export const DEFAULT_SORT_DIR: SortDirection = 'DESC';
